import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { AppliedFilter } from '../components/pages/collateral/shared/FilterBar';

export type FilterKey = 'guaranteeStatus' | 'intensity' | 'operationType' | 'obligationType' | 'validationStatus' | 'finalStatus';

const STORAGE_KEY = 'collateral:verification-config:filters';
const STORAGE_KEY_INACTIVE = 'collateral:verification-config:showInactive';

function loadPersistedFilters(): AppliedFilter[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const keys: string[] = parsed.activeKeys || [];
            const vals: Record<string, string> = parsed.values || {};
            return keys
                .filter(k => vals[k] && vals[k].trim())
                .map(k => ({ key: k, value: vals[k] }));
        }
    } catch { /* ignore */ }
    return [];
}

function loadPersistedShowInactive(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY_INACTIVE) === 'true';
    } catch { /* ignore */ }
    return false;
}

export default function useVerificationConfigPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [activeFilters, setActiveFilters] = useState<AppliedFilter[]>(loadPersistedFilters);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [showInactive, setShowInactive] = useState(loadPersistedShowInactive);
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 100;
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    const buildFilterParams = useCallback((filters: AppliedFilter[]) => {
        const params: Record<string, string | undefined> = {};
        for (const f of filters) {
            const val = f.value;
            if (val) params[f.key] = val;
        }
        return params;
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const filterParams = buildFilterParams(activeFilters);
            const result = await collateralApi.getVerificationConfigs({
                page, pageSize,
                search: search || undefined,
                guaranteeStatus: filterParams.guaranteeStatus,
                intensity: filterParams.intensity,
                operationType: filterParams.operationType,
                obligationType: filterParams.obligationType,
                validationStatus: filterParams.validationStatus,
                finalStatus: filterParams.finalStatus,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
                showInactive: showInactive ? 'true' : undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, activeFilters, search, sortField, sortOrder, showInactive, navigate, buildFilterParams]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    // Persist showInactive in separate localStorage key
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_INACTIVE, showInactive ? 'true' : 'false');
        } catch { /* ignore */ }
    }, [showInactive]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handleFilterSearch = useCallback((filters: AppliedFilter[]) => {
        setActiveFilters(filters);
        setPage(1);
    }, []);

    const handleRowClick = (row: any) => {
        navigate(`/collateral/verification-config/${row.id}/view`);
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, activeFilters,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        showInactive, setShowInactive,
        refresh, handleFilterSearch, handleRowClick,
    };
}
